/**
 * Talk-session chat helpers: merge both sides' messages and treat 429s
 * as a pause, not a session glitch.
 */

import { isHardSessionEnd } from './sessionNotifications';

export const SESSION_POLL_MS = 2500;
export const SESSION_IDLE_POLL_MS = 15000;
export const SESSION_POLL_MAX_MS = 30000;
export const SESSION_RECONNECT_RETRY_MS = 8000;
export const SESSION_RECONNECT_GRACE_MS = 60 * 1000;
export const SESSION_JOIN_TIMEOUT_MS = 8000;

/**
 * A 429 on send or poll is not a dropped socket. Only a failed Join
 * before the room is open should show the reconnect notice.
 *
 * @param {{ open?: boolean, fromSend?: boolean, sendSucceeded?: boolean }} [state]
 * @returns {boolean}
 */
export const shouldShowDisconnectedNotice = ({
  open = false,
  fromSend = false,
  sendSucceeded = false,
} = {}) => {
  if (sendSucceeded || fromSend || open) {
    return false;
  }
  return true;
};

export const isApiRateLimited = error =>
  error?.code === 'rate_limited' ||
  error?.status === 429 ||
  error?.sharetribeStatus === 429 ||
  /status code 429/i.test(`${error?.error || ''} ${error?.message || ''}`) ||
  /too many requests/i.test(`${error?.error || ''} ${error?.message || ''}`);

export const sessionRetryAfterMs = error => {
  const seconds = Number(error?.retryAfter);
  return (Number.isFinite(seconds) && seconds > 0 ? seconds : 30) * 1000;
};

export const nextSessionPollMs = (error, previousMs = SESSION_POLL_MS) => {
  if (!isApiRateLimited(error)) {
    return previousMs || SESSION_POLL_MS;
  }
  const floor = Math.max(sessionRetryAfterMs(error), previousMs * 2);
  return Math.min(floor, SESSION_POLL_MAX_MS);
};

/**
 * Fast poll only while a talk session is live. Hidden / ended tabs stop.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.ended]
 * @param {boolean} [opts.hidden]
 * @param {boolean} [opts.live]
 * @returns {number|null} delay ms, or null to skip this tick
 */
export const sessionPagePollMs = ({ ended = false, hidden = false, live = false } = {}) => {
  if (ended) {
    return null;
  }
  if (hidden) {
    return null;
  }
  return live ? SESSION_POLL_MS : SESSION_IDLE_POLL_MS;
};

/**
 * Clock running or both people already in the room.
 *
 * @param {Object} [session]
 * @returns {boolean}
 */
export const isLiveTalkSession = (session = {}) =>
  session?.clockStarted === true ||
  session?.bothJoined === true ||
  (!!session?.roomName && session?.ended !== true);

const messageKey = message => {
  if (message?.sharetribeMessageId) {
    return `st:${message.sharetribeMessageId}`;
  }
  if (message?.id) {
    return `id:${message.id}`;
  }
  return `body:${message?.senderId || ''}|${(message?.body || '').trim()}`;
};

/**
 * Keep local messages when a poll is empty or partial so a 429 / store miss
 * cannot wipe the other person's chat from the screen.
 *
 * @param {Array} current
 * @param {Array|undefined} incoming
 * @returns {Array}
 */
export const mergeSessionMessages = (current = [], incoming) => {
  const existing = Array.isArray(current) ? current : [];
  if (!Array.isArray(incoming)) {
    return existing;
  }
  const byKey = new Map();
  existing.forEach(message => {
    if (message) {
      byKey.set(messageKey(message), message);
    }
  });
  incoming.forEach(message => {
    if (!message) {
      return;
    }
    const key = messageKey(message);
    const prev = byKey.get(key);
    byKey.set(key, prev ? { ...prev, ...message } : message);
  });
  return [...byKey.values()].sort((a, b) => {
    const am = Date.parse(a?.createdAt) || 0;
    const bm = Date.parse(b?.createdAt) || 0;
    return am - bm;
  });
};

/**
 * True when this viewer already joined the live room on the server.
 * Poll / show must not count as Join.
 *
 * @param {object|null} payload
 * @param {{ role?: string, currentUserId?: string }} [viewer]
 * @returns {boolean}
 */
export const viewerHasJoinedSession = (payload, viewer = {}) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const role = viewer.role || payload.role || null;
  if (role === 'listener' || role === 'provider') {
    return payload.listenerJoined === true;
  }
  if (role === 'talker' || role === 'customer') {
    return payload.talkerJoined === true;
  }
  if (viewer.currentUserId && payload.providerId === viewer.currentUserId) {
    return payload.listenerJoined === true;
  }
  if (viewer.currentUserId && payload.customerId === viewer.currentUserId) {
    return payload.talkerJoined === true;
  }
  return false;
};

/**
 * A 429 must not be a dead end — but an empty poll is not a join.
 * Only reopen when this viewer already joined, the clock is running, or
 * there is a live room / thread.
 *
 * @param {object|null} payload
 * @param {{ role?: string, currentUserId?: string }} [viewer]
 * @returns {boolean}
 */
export const canOpenSessionFromPayload = (payload, viewer = {}) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (isHardSessionEnd(payload)) {
    return false;
  }
  if (payload.clockStarted === true || payload.bothJoined === true) {
    return true;
  }
  if (payload.roomName || payload.voiceEmbedUrl || payload.videoEmbedUrl) {
    return true;
  }
  if (Array.isArray(payload.messages) && payload.messages.length > 0) {
    return true;
  }
  return viewerHasJoinedSession(payload, viewer);
};

/**
 * Other-party live presence from the session poll.
 *
 * @param {object|null} session
 * @returns {'waiting'|'in'|'left'|null}
 */
export const peerPresenceFromSession = session => {
  const value = session?.otherPresence;
  if (value === 'waiting' || value === 'in' || value === 'left') {
    return value;
  }
  return null;
};

/**
 * One notice when the other person joins, rejoins, or leaves.
 *
 * @param {'waiting'|'in'|'left'|null} previous
 * @param {'waiting'|'in'|'left'|null} next
 * @returns {'joined'|'rejoined'|'left'|null}
 */
export const peerPresenceNotice = (previous, next) => {
  if (!previous || !next || previous === next) {
    return null;
  }
  if (next === 'in') {
    return previous === 'left' ? 'rejoined' : 'joined';
  }
  if (next === 'left') {
    return 'left';
  }
  return null;
};

/**
 * Display name for leave/join copy. Empty when unknown.
 *
 * @param {string} [name]
 * @returns {string}
 */
export const peerLeaveDisplayName = name => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed;
};

/**
 * Status line about the other person. The server's 'waiting' means WE are
 * waiting for THEM (they have not joined yet), so it must never read as
 * "{name} is waiting" (Oct 6: talker saw "Lauren Sophia is waiting" while the
 * listener had not joined). Only 'in' says they are here.
 *
 * @param {'waiting'|'in'|'left'|null} presence
 * @param {{ hasName?: boolean }} [options]
 * @returns {string|null}
 */
export const peerPresenceStatusId = (presence, { hasName } = {}) => {
  if (presence === 'in') {
    return hasName ? 'SessionPanel.peerInNamed' : 'SessionPanel.peerIn';
  }
  if (presence === 'left') {
    return hasName ? 'SessionPanel.peerLeft' : 'SessionPanel.peerLeftUnknown';
  }
  if (presence === 'waiting') {
    return hasName ? 'SessionPanel.peerNotJoined' : 'SessionPanel.peerNotJoinedUnknown';
  }
  return null;
};

export const peerPresenceNoticeId = (notice, { hasName } = {}) => {
  if (notice === 'joined') {
    return 'SessionPanel.peerJoinedNotice';
  }
  if (notice === 'rejoined') {
    return 'SessionPanel.peerRejoinedNotice';
  }
  if (notice === 'left') {
    return hasName ? 'SessionPanel.peerLeftNotice' : 'SessionPanel.peerLeftNoticeUnknown';
  }
  return null;
};

export const applySessionPayload = (current, payload) => {
  if (!payload) {
    return current || null;
  }
  return {
    ...(current || {}),
    ...payload,
    messages: mergeSessionMessages(current?.messages, payload.messages),
    glitchEligible: payload.glitchEligible === true,
  };
};
